<div
  class="scw-search-field"
  matAutocompleteOrigin
  #searchOrigin="matAutocompleteOrigin"
  [class.expanded]="searchMode() === ESearchMode.extended"
>
  <mat-icon>search</mat-icon>
  <input
    aria-label="Search"
    [placeholder]="'field.search' | translate"
    [matAutocomplete]="searchResult"
    [matAutocompleteConnectedTo]="searchOrigin"
    [formControl]="searchControl"
    #searchTrigger="matAutocompleteTrigger"
    autocomplete="off"
    (focus)="expandSearch()"
  />
</div>

<mat-autocomplete #searchResult="matAutocomplete" class="home-search">
  @if ((searchStore.searchStatus$ | async) === searchStore.ELoadStatus.loading) {
    <mat-progress-bar class="search-progress-bar" mode="indeterminate"></mat-progress-bar>
  }

  @let query = searchControl.value;
  @let hasBoards = (boardSearchResults$ | async)?.length;
  @let hasIssues = (issueSearchResults$ | async)?.length;
  @let hasActions = (actionSearchResults$ | async)?.length;
  @let hasComments = (itemCommentSearchResults$ | async)?.length;
  @let hasValues = (itemValueSearchResults$ | async)?.length;
  @let hasDescriptions = (itemDescriptionSearchResults$ | async)?.length;
  @let hasAnyResult = hasBoards || hasIssues || hasActions || hasComments || hasValues || hasDescriptions;
  @let debounceSettled = (searchObs$ | async) === query;
  @let searchSucceeded = (searchStore.searchStatus$ | async) === searchStore.ELoadStatus.success;

  @if (!query) {
    <mat-option class="search-state" disabled>
      <div class="search-state-box">
        <span class="search-state-icon"><mat-icon class="material-icons-outlined">travel_explore</mat-icon></span>
        <span class="search-state-title">{{ 'page.globalSearch.promptTitle' | translate }}</span>
        <span class="search-state-subtitle">{{ 'page.globalSearch.promptHint' | translate }}</span>
      </div>
    </mat-option>
  } @else if (query.length < minSearchChars) {
    <mat-option class="search-state" disabled>
      <div class="search-state-box">
        <span class="search-state-icon"><mat-icon class="material-icons-outlined">keyboard</mat-icon></span>
        <span class="search-state-subtitle">
          {{ 'system.message.minSearchChars' | translate: { count: minSearchChars } }}
        </span>
      </div>
    </mat-option>
  } @else if (debounceSettled && searchSucceeded && !hasAnyResult) {
    <mat-option class="search-state" disabled>
      <div class="search-state-box">
        <span class="search-state-icon empty"><mat-icon class="material-icons-outlined">search_off</mat-icon></span>
        <span class="search-state-title">{{ 'page.globalSearch.noResultsTitle' | translate }}</span>
        <span class="search-state-subtitle">{{ 'page.globalSearch.noResultsHint' | translate }}</span>
      </div>
    </mat-option>
  }

  @if (hasBoards) {
    <mat-optgroup label="{{ 'page.globalSearch.boardGroup' | translate }}">
      @for (result of boardSearchResults$ | async; track result) {
        <mat-option [value]="result.name" (click)="navigateToBoard(result.key)">
          <div class="search-result-row">
            <span class="search-result-icon board"><mat-icon class="material-icons-outlined">grid_view</mat-icon></span>
            <span class="search-result-text">
              <span class="search-result-title">{{ result.name }}</span>
              <span class="search-result-subtitle">{{ result.key }}</span>
            </span>
          </div>
        </mat-option>
      }
      @if (searchStore.isLoadMoreAvailableForBoards$ | async) {
        <mat-option class="search-load-more">
          <button type="button" class="search-load-more-button" (click)="loadMoreSearchResults('boards', $event)">
            <span class="search-load-more-label">{{ 'page.home.loadMore' | translate }}</span>
          </button>
        </mat-option>
      }
    </mat-optgroup>
  }

  @if (hasIssues) {
    <mat-optgroup label="{{ 'page.globalSearch.issueGroup' | translate }}">
      @for (result of issueSearchResults$ | async; track result) {
        <mat-option [value]="result.name" (click)="openItemEditModal(result.id, true)">
          <div class="search-result-row">
            <span class="search-result-icon issue">
              <mat-icon class="material-icons-outlined">assignment_late</mat-icon>
            </span>
            <span class="search-result-text">
              <span class="search-result-title">{{ result.name }}</span>
              <span class="search-result-subtitle">{{ result.key }}</span>
            </span>
          </div>
        </mat-option>
      }
      @if (searchStore.isLoadMoreAvailableForIssues$ | async) {
        <mat-option class="search-load-more">
          <button type="button" class="search-load-more-button" (click)="loadMoreSearchResults('issue', $event)">
            <span class="search-load-more-label">{{ 'page.home.loadMore' | translate }}</span>
          </button>
        </mat-option>
      }
    </mat-optgroup>
  }

  @if (hasActions) {
    <mat-optgroup label="{{ 'page.globalSearch.actionGroup' | translate }}">
      @for (result of actionSearchResults$ | async; track result) {
        <mat-option [value]="result.name" (click)="openItemEditModal(result.id, true)">
          <div class="search-result-row">
            <span class="search-result-icon action">
              <mat-icon class="material-icons-outlined">directions_run</mat-icon>
            </span>
            <span class="search-result-text">
              <span class="search-result-title">{{ result.name }}</span>
              <span class="search-result-subtitle">{{ result.key }}</span>
            </span>
          </div>
        </mat-option>
      }
      @if (searchStore.isLoadMoreAvailableForActions$ | async) {
        <mat-option class="search-load-more">
          <button type="button" class="search-load-more-button" (click)="loadMoreSearchResults('action', $event)">
            <span class="search-load-more-label">{{ 'page.home.loadMore' | translate }}</span>
          </button>
        </mat-option>
      }
    </mat-optgroup>
  }

  @if (hasComments) {
    <mat-optgroup label="{{ 'page.globalSearch.commentsGroup' | translate }}">
      @for (result of itemCommentSearchResults$ | async; track result) {
        <mat-option [value]="result?.item?.name" (click)="openItemEditModal(result?.item?.id, true)">
          <div class="search-result-row">
            <span class="search-result-icon comment">
              <mat-icon class="material-icons-outlined">chat_bubble</mat-icon>
            </span>
            <span class="search-result-text">
              <span class="search-result-title">{{ result?.item?.key }} {{ result?.item?.name }}</span>
              <span class="search-result-subtitle search-comment-row">
                {{ 'field.comment' | translate }}: <span [innerHTML]="getSnippetHtml(result.description)"></span>
              </span>
              @if (result?.commentCount; as count) {
                @if (count > 1) {
                  <span class="search-result-subtitle">
                    {{ 'field.otherCommentsInfo' | translate: { count: count - 1 } }}
                  </span>
                }
              }
            </span>
          </div>
        </mat-option>
      }
      @if (searchStore.isLoadMoreAvailableForActions$ | async) {
        <mat-option class="search-load-more">
          <button type="button" class="search-load-more-button" (click)="loadMoreSearchResults('itemComment', $event)">
            <span class="search-load-more-label">{{ 'page.home.loadMore' | translate }}</span>
          </button>
        </mat-option>
      }
    </mat-optgroup>
  }

  @if (hasValues) {
    <mat-optgroup label="{{ 'page.globalSearch.fieldValuesGroup' | translate }}">
      @for (result of itemValueSearchResults$ | async; track result) {
        <mat-option [value]="result.name" (click)="openItemEditModal(result.id, true)">
          <div class="search-result-row">
            <span class="search-result-icon value">
              <mat-icon class="material-icons-outlined">sell</mat-icon>
            </span>
            <span class="search-result-text">
              <span class="search-result-title">{{ result.key }} {{ result.name }}</span>
              @let matched = getMatchedValueField(result);
              @if (matched) {
                <span class="search-result-subtitle search-comment-row"> {{ matched.fieldName }}: {{ matched.value }} </span>
              } @else {
                <span class="search-result-subtitle">{{ result.key }}</span>
              }
            </span>
          </div>
        </mat-option>
      }
      @if (searchStore.isLoadMoreAvailableForItemValues$ | async) {
        <mat-option class="search-load-more">
          <button type="button" class="search-load-more-button" (click)="loadMoreSearchResults('itemValue', $event)">
            <span class="search-load-more-label">{{ 'page.home.loadMore' | translate }}</span>
          </button>
        </mat-option>
      }
    </mat-optgroup>
  }

  @if (hasDescriptions) {
    <mat-optgroup label="{{ 'page.globalSearch.descriptionGroup' | translate }}">
      @for (result of itemDescriptionSearchResults$ | async; track result) {
        <mat-option [value]="result.name" (click)="openItemEditModal(result.id, true)">
          <div class="search-result-row">
            <span class="search-result-icon description">
              <mat-icon class="material-icons-outlined">notes</mat-icon>
            </span>
            <span class="search-result-text">
              <span class="search-result-title">{{ result.key }} {{ result.name }}</span>
              <span class="search-result-subtitle search-comment-row">
                {{ 'field.description' | translate }}: <span [innerHTML]="getSnippetHtml(result.description)"></span>
              </span>
            </span>
          </div>
        </mat-option>
      }
      @if (searchStore.isLoadMoreAvailableForItemDescriptions$ | async) {
        <mat-option class="search-load-more">
          <button
            type="button"
            class="search-load-more-button"
            (click)="loadMoreSearchResults('itemDescription', $event)"
          >
            <span class="search-load-more-label">{{ 'page.home.loadMore' | translate }}</span>
          </button>
        </mat-option>
      }
    </mat-optgroup>
  }
</mat-autocomplete>
